import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Alert, Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UserApiService from '../../api/UserApiService';
import authService from '../../auth/AuthService';
import AuthUtil from '../Utils/AuthUtil';
import SaveButton from '../Common/SaveButton';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';

const userApiService = new UserApiService();

/**
 * Super-admin shortcut for starting an impersonation session from anywhere in the app, without
 * having to find the user in the admin grid first.
 */
export default function ImpersonateUserModal (props) {
    const { open, onClose, setSnackbarProperties } = props;
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const currentUserId = useMemo(() => Number(AuthUtil.getUserId(userToken)), [userToken]);

    // Mirror the rules the server enforces, so the list only offers users it will actually accept.
    const selectableUsers = useMemo(() => users.filter((user) =>
        Boolean(user.mainSettingsDto.usActive)
        && !user.mainSettingsDto.isSuperAdmin
        && user.usId !== currentUserId), [users, currentUserId]);

    useEffect(() => {
        async function loadUsers () {
            setLoading(true);
            const response = await userApiService.getUsers();
            if (response && response.success) {
                setUsers(response.data);
            } else {
                setSnackbarProperties(response?.message ?? t('impersonateModal.loadError'), 'error');
            }
            setLoading(false);
        }

        if (open) {
            setSelectedUser(null);
            void loadUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /** Everything the admin might recognise the user by, so one search box covers all of it. */
    const optionLabel = (user) => {
        const name = [user.mainSettingsDto.usFirstname, user.mainSettingsDto.usLastname]
            .filter(Boolean).join(' ');
        return [user.mainSettingsDto.usUsername, name, user.mainSettingsDto.usEmail, user.userOrg?.ogName]
            .filter(Boolean).join(' — ');
    };

    const { run: startImpersonation, loading: starting } = useAsyncCallback(async () => {
        if (!selectedUser) {
            return;
        }

        const response = await authService.impersonate(selectedUser.usId);
        if (response && response.success) {
            // Full reload so nothing fetched as the super admin survives into the new session.
            window.location.href = '/';
            return;
        }

        setSnackbarProperties(response?.message ?? t('impersonateModal.startError'), 'error');
    });

    return (
        <Dialog open={open} fullWidth maxWidth="sm">
            <DialogTitle>{t('impersonateModal.title')}</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('impersonateModal.explanation')}
                </Alert>
                {loading
                    ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                    : <Autocomplete
                        autoHighlight
                        options={selectableUsers}
                        value={selectedUser}
                        onChange={(event, value) => setSelectedUser(value)}
                        getOptionLabel={optionLabel}
                        isOptionEqualToValue={(option, value) => option.usId === value.usId}
                        noOptionsText={t('impersonateModal.noMatches')}
                        renderOption={(optionProps, user) => (
                            <Box component="li" {...optionProps} key={user.usId}>
                                <Box>
                                    <Typography variant="body2">
                                        {user.mainSettingsDto.usUsername}
                                        {user.userOrg?.ogName ? ` — ${user.userOrg.ogName}` : ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {[user.mainSettingsDto.usFirstname, user.mainSettingsDto.usLastname]
                                            .filter(Boolean).join(' ')}
                                        {user.mainSettingsDto.usEmail ? ` · ${user.mainSettingsDto.usEmail}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} autoFocus label={t('impersonateModal.searchLabel')} />
                        )}
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={starting}>{t('common.cancel')}</Button>
                <SaveButton
                    loading={starting}
                    disabled={!selectedUser}
                    onClick={startImpersonation}
                >
                    {t('impersonateModal.startButton')}
                </SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ImpersonateUserModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    setSnackbarProperties: PropTypes.func.isRequired,
};